import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import StarIcon from '@mui/icons-material/Star';
import { ref, push } from 'firebase/database';
import { database } from '../firebase';

const subjectsList = [
  'Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'English',
  'Science (Junior)',
  'Social Science',
  'Hindi',
  'Logical Reasoning',
  'Computer Science'
];

const classesList = [
  'Primary School (Classes 1–5)',
  'Middle School (Classes 6–8)',
  'Secondary School (Classes 9–10)',
  'Senior Secondary (Classes 11–12)',
  'JEE',
  'NEET',
];

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Enter valid 10-digit number')
    .required('Phone is required'),
  city: Yup.string().required('City is required'),
  subject: Yup.string().required('Select subject'),
  experience: Yup.string().required('Experience required'),
  qualification: Yup.string().required('Qualification required'),
});

const Careers = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const theme = useTheme();

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      city: '',
      subject: '',
      classCategory: '',
      experience: '',
      qualification: '',
      message: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const careersRef = ref(database, 'teacherEnrolments');
        await push(careersRef, {
          ...values,
          timestamp: new Date().toISOString(),
          status: 'pending',
        });

        setSnackbarMessage('Application submitted successfully!');
        setSnackbarSeverity('success');
        formik.resetForm();
      } catch (error) {
        setSnackbarMessage('Error submitting application.');
        setSnackbarSeverity('error');
      }
      setOpenSnackbar(true);
    },
  });

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        
        {/* LEFT SECTION */}
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom>
            Join as a Subject Teacher (Class 1–12 | JEE | NEET)
          </Typography>

          <Typography variant="subtitle1" color="text.secondary" paragraph>
            Teach students across India. Earn, grow, and build your teaching brand.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Why Join Us?
            </Typography>

            <Grid container spacing={2}>
              {[
                'High earning potential',
                'Teach JEE / NEET aspirants',
                'Work from Office/ Home',
                'Flexible teaching hours',
                'Olympiad based syllabus',
                'Access to quality students',
                'Performance-based incentives',
                'Growth opportunities',
              ].map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="body2">{item}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* FORM SECTION */}
        <Grid item xs={12} md={6}>
          <Card elevation={4}>
            <CardContent>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Full Name"
                      {...formik.getFieldProps('name')}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      {...formik.getFieldProps('email')}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Phone Number"
                      {...formik.getFieldProps('phone')}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="city"
                      label="City"
                      {...formik.getFieldProps('city')}
                      error={formik.touched.city && Boolean(formik.errors.city)}
                      helperText={formik.touched.city && formik.errors.city}
                    />
                  </Grid>

                  {/* SUBJECT */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Subject</InputLabel>
                      <Select
                        name="subject"
                        value={formik.values.subject}
                        onChange={formik.handleChange}
                      >
                        {subjectsList.map((sub) => (
                          <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* CLASS */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Classes You Can Teach</InputLabel>
                      <Select
                        name="classCategory"
                        value={formik.values.classCategory}
                        onChange={formik.handleChange}
                      >
                        {classesList.map((cls) => (
                          <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* EXPERIENCE */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="experience"
                      label="Years of Experience"
                      {...formik.getFieldProps('experience')}
                    />
                  </Grid>

                  {/* QUALIFICATION */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="qualification"
                      label="Highest Qualification"
                      {...formik.getFieldProps('qualification')}
                    />
                  </Grid>

                  {/* MESSAGE */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="message"
                      label="Additional Information (Optional)"
                      {...formik.getFieldProps('message')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                    >
                      Apply Now
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Careers;